import React from 'react';
import cn from 'classnames';
import { FormikProps, FormikBag, withFormik, WithFormikConfig } from 'formik';
import { HeaderText, TextInput2, Button, Select2, Checkbox } from '@el8/vital';
import { Box, Card } from 'components';
import { createValidator } from 'utils/createValidator';
import { required, dateOfBirth } from 'utils/validators';
import { formatDob } from 'utils/format';

import { PatientSex } from 'types';
import styles from './NewPatientForm.less';

type FormValues = {
  delegate: boolean;
  dob: string;
  firstName: string;
  lastName: string;
  sex: PatientSex;
};

type OwnProps = {
  createPatient: (patientInfo: FormValues) => Promise<void>;
};

type Props = OwnProps & FormikProps<FormValues>;

const validate = createValidator({
  dob: [required, dateOfBirth],
  firstName: required,
  lastName: required,
  sex: required,
});

const EMPTY_PATIENT: FormValues = {
  delegate: false,
  dob: '',
  firstName: '',
  lastName: '',
  sex: undefined,
};

const formikConfig: WithFormikConfig<OwnProps, FormValues> = {
  mapPropsToValues(): FormValues {
    return EMPTY_PATIENT;
  },
  validate,
  handleSubmit(patientInfo: FormValues, formikBag: FormikBag<Props, FormValues>): void {
    formikBag.props.createPatient(patientInfo).finally((): void => formikBag.setSubmitting(false));
  },
};

const NewPatientForm: React.FC<Props> = ({
  errors,
  handleChange,
  handleBlur,
  handleSubmit,
  isSubmitting,
  isValid,
  setFieldTouched,
  setFieldValue,
  touched,
  values,
}) => {
  return (
    <Card>
      <HeaderText legacyVariant="legacyTitleText" sizing="heading-lg" tag="h1">
        Add Patient
      </HeaderText>
      <form onSubmit={handleSubmit} className={styles.form}>
        <Box direction="column">
          <Box>
            <TextInput2
              disabled={isSubmitting}
              name="firstName"
              error={touched.firstName && errors.firstName}
              value={values.firstName}
              label="First Name"
              onChange={handleChange}
              onBlur={handleBlur}
              className={cn(styles.field, styles.withRightMargin)}
            />
            <TextInput2
              disabled={isSubmitting}
              name="lastName"
              error={touched.lastName && errors.lastName}
              value={values.lastName}
              label="Last Name"
              onChange={handleChange}
              onBlur={handleBlur}
              className={styles.field}
            />
          </Box>
          <Box>
            <TextInput2
              disabled={isSubmitting}
              name="dob"
              error={touched.dob && errors.dob}
              value={formatDob(values.dob)}
              label="Date of Birth"
              placeholder="MM/DD/YYYY"
              /**
               * A little bit of hack here. In order to show a special keypad on mobile phone we need to
               * set an appropriate text input type, i.e. "date", "number", "tel". Problem with "date"
               * and "number" is that mobile safari and chrome (mobile and desktop) break styling of our
               * text input field and make page look bad. The only option that we have is a "tel" type
               * which semantically is not correct, but does the thing.
               */
              type="tel"
              onChange={handleChange}
              onBlur={handleBlur}
              className={cn(styles.field, styles.withRightMargin)}
            />
            <Select2
              disabled={isSubmitting}
              floatingLabel="Gender"
              error={touched.sex && errors.sex}
              onBlur={(): void => setFieldTouched('sex')}
              onChange={(value: string): void => {
                setFieldValue('sex', value);
              }}
              value={values.sex}
              items={[
                { value: PatientSex.female, label: 'Female' },
                { value: PatientSex.male, label: 'Male' },
                { value: PatientSex.unspecified, label: 'Unspecified' },
              ]}
              width="50%"
              style={{ flexShrink: 0 }}
            />
          </Box>
          <Checkbox
            disabled={isSubmitting}
            name="delegate"
            checked={values.delegate}
            label="I am the legal representative of this patient"
          />
          <Button
            className={styles.addPatient}
            type="submit"
            kind="primary"
            disabled={!isValid || isSubmitting}
          >
            Add Patient
          </Button>
        </Box>
      </form>
    </Card>
  );
};

export default withFormik(formikConfig)(NewPatientForm);
